import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import './TeacherHours.css';
import TeacherPaymentReport from './TeacherPaymentReport';
import TutorClassLogPayment from './TutorClassLogPayment';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  Input, 
  Card, 
  Tabs, 
  Typography, 
  Alert, 
  Space 
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { TabPane } = Tabs;

const TeacherHoursAdmin = () => {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('hours');
  const [loading, setLoading] = useState(false);
  const pageSize = 10; // Default Ant Design page size
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teachers`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setTeachers(response.data);
        setLoading(false);
      } catch (error) {
        console.error(t('errorFetchingTeachers'), error);
        setError(t('failedToFetchTeachers'));
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [API_URL, t]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredTeachers = teachers.filter(teacher =>
    (teacher.firstname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (teacher.lastname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (teacher.userid?.toString() || '').includes(searchTerm)
  );

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // Define columns for Ant Design Table
  const columns = [
    {
      title: t('teacherID'),
      dataIndex: 'userid',
      key: 'userid',
      sorter: (a, b) => a.userid - b.userid,
      width: 100,
    },
    {
      title: t('teacherName'),
      key: 'name',
      render: (_, record) => (
        <Link to={`/teacher-details/${record.userid}`}>
          ({record.nickname || ''}) {record.firstname || ''} {record.lastname || ''}
        </Link>
      ),
      sorter: (a, b) => 
        `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`),
    },
    {
      title: t('hourlyRate'),
      dataIndex: 'hoursrate',
      key: 'hoursrate',
      sorter: (a, b) => a.hoursrate - b.hoursrate,
      width: 150,
    },
  ];

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="teacher-hours-container">
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          type="card"
          size="large"
          className="teacher-tabs"
        >
          <TabPane tab={t('teacherHours')} key="hours">
            <Card className="teacher-hours-card">
              <Title level={3}>{t('tutorHourlyRates')}</Title>
              
              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              
              <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                <Input
                  placeholder={t('searchByNameOrID')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  prefix={<SearchOutlined />}
                  allowClear
                  style={{ maxWidth: 300 }}
                />
              </Space>
              
              <Table
                columns={columns}
                dataSource={filteredTeachers}
                rowKey="userid"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page) => setCurrentPage(page),
                  total: filteredTeachers.length,
                  showSizeChanger: false,
                }}
                loading={loading}
                size="middle"
                bordered
              />
            </Card>
          </TabPane>
          
          <TabPane tab={t('paymentReport')} key="payment">
            <TeacherPaymentReport />
          </TabPane>
          
          <TabPane tab={t('tutorclasslogs')} key="logs">
            <TutorClassLogPayment />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default withRoleAccess(TeacherHoursAdmin, ['superadmin']);